import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { activate } from "../../../utils/keyboard-activate";
import { useParams, useNavigate } from 'react-router-dom';
import {
  getBatchExtraction,
  updateRecords,
  deleteRecord,
  addManualRecord,
  getPageImageUrl,
  retryExtraction,
  retryPage,
  deletePage,
  updatePageTeamName,
  updatePageTeamNames,
  setRecordsJuco,
  fillRecordsJucoYear,
  describeJucoActionError,
} from '../../../services/batch-extractions';
import type {
  BatchExtraction,
  BatchExtractionRecord,
  BatchExtractionRecordUpdate,
  BatchExtractionPage,
  JucoScope,
  PageTeamNameUpdate,
} from '../../../types/batch-extraction';
import { CLEAR_RESOLVED_TEAM } from '../../../types/batch-extraction';
import type { TeamOption } from '../../../components/TeamTypeahead';
import { DistributeModal } from './components/DistributeModal';
import { ManualEntryModal } from './components/ManualEntryModal';
import { ImageZoomViewer } from './components/ImageZoomViewer';
import { ResizeHandle } from './components/ResizeHandle';
import { RecordRow } from './components/RecordRow';
import { BulkSetTeamModal } from './components/BulkSetTeamModal';
import { BulkJucoYearModal } from './components/BulkJucoYearModal';
import { BatchWideJucoActions } from './components/BatchWideJucoActions';
import { JUCO_PLAYER_LEVEL } from '../../../constants/playerLevels';
import type { JucoYear } from '../../../utils/roster-class';
import { AdminPage, AdminPageHeader } from '../AdminPage';
import './BatchExtractionReview.css';

type EditingCell = {
  recordId: string;
  field: keyof BatchExtractionRecord;
} | null;

// Datalist backing the per-page team-name combobox — suggests teams already
// present elsewhere in this batch so a teamless page can be grouped under one.
const PAGE_TEAM_DATALIST_ID = 'batch-review-page-team-datalist';

const EDITABLE_FIELDS: { key: keyof BatchExtractionRecord; label: string; width: string }[] = [
  { key: 'teamName', label: 'Team', width: '100px' },
  { key: 'jerseyNumber', label: '#', width: '45px' },
  { key: 'firstName', label: 'First', width: '80px' },
  { key: 'lastName', label: 'Last', width: '80px' },
  { key: 'gradYear', label: 'Grad Year', width: '60px' },
  { key: 'jucoYear', label: 'JC Yr', width: '50px' },
  { key: 'position', label: 'Pos', width: '50px' },
  { key: 'bats', label: 'B', width: '35px' },
  { key: 'throws', label: 'T', width: '35px' },
  { key: 'height', label: 'Ht', width: '50px' },
  { key: 'weight', label: 'Wt', width: '45px' },
  { key: 'phone', label: 'Phone', width: '100px' },
  { key: 'email', label: 'Email', width: '140px' },
  { key: 'city', label: 'City', width: '80px' },
  { key: 'state', label: 'St', width: '35px' },
  { key: 'address', label: 'Address', width: '160px' },
  { key: 'school', label: 'School', width: '100px' },
];

const formatRetryStatus = (status: string): string => {
  switch (status) {
    case 'pending':
      return 'Queued...';
    case 'assessing':
      return 'Assessing...';
    case 'extracting':
      return 'Extracting...';
    default:
      return 'Processing...';
  }
};

export const BatchExtractionReview = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [batch, setBatch] = useState<BatchExtraction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editingCell, setEditingCell] = useState<EditingCell>(null);
  const [pendingUpdates, setPendingUpdates] = useState<Map<string, BatchExtractionRecordUpdate>>(new Map());
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterTeam, setFilterTeam] = useState<string>('');

  const [showDistributeModal, setShowDistributeModal] = useState(false);
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Per-record team assignment (resolvedTeamId). Display names are cached here
  // because the API returns only the resolved team's Guid on each record, not
  // its name. Records flagged by the commit gate live in `unresolvedRecordIds`.
  const [assignedTeamNames, setAssignedTeamNames] = useState<Map<string, string>>(new Map());
  const [unresolvedRecordIds, setUnresolvedRecordIds] = useState<Set<string>>(new Set());
  // Records the commit gate flagged as JUCO with no JC year. Separate from the team gate above
  // because the fix is different — a year to fill, not a team to pick.
  const [jucoYearMissingIds, setJucoYearMissingIds] = useState<Set<string>>(new Set());
  const [jucoSubmitting, setJucoSubmitting] = useState(false);
  const [showJucoYearModal, setShowJucoYearModal] = useState(false);

  // Side-by-side view state
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [pageImageUrl, setPageImageUrl] = useState<string | null>(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [deletingPageId, setDeletingPageId] = useState<string | null>(null);
  const [retryingPage, setRetryingPage] = useState<{ id: string; status: string } | null>(null);

  // Collapse state
  const [statsCollapsed, setStatsCollapsed] = useState(false);
  const [pagesCollapsed, setPagesCollapsed] = useState(false);

  // Page team name editing state
  const [editingPageTeam, setEditingPageTeam] = useState<string | null>(null);
  const [pageTeamValue, setPageTeamValue] = useState('');

  // Bulk page selection + team-name assignment
  const [selectedPageIds, setSelectedPageIds] = useState<Set<string>>(new Set());
  const [showBulkTeamModal, setShowBulkTeamModal] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // Resizable panels state
  const [imagePanelWidth, setImagePanelWidth] = useState(40); // percentage
  const splitViewRef = useRef<HTMLDivElement>(null);

  // Polling for extraction progress
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  const isExtractionInProgress = (status: string) => {
    return status === 'splitting' || status === 'uploading' || status === 'extracting';
  };

  const startPolling = useCallback(() => {
    if (pollIntervalRef.current || !id) return; // Already polling or no id

    pollIntervalRef.current = setInterval(async () => {
      try {
        const updated = await getBatchExtraction(id);
        setBatch(updated);

        // Stop polling when extraction is done
        if (!isExtractionInProgress(updated.status)) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      } catch (err) {
        console.error('Failed to poll status:', err);
      }
    }, 2000);
  }, [id]);

  const fetchBatch = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getBatchExtraction(id);
      setBatch(response);

      // Auto-select first completed page with records, or first completed page
      if (response.pages.length > 0) {
        const completedPages = response.pages.filter(p => p.status === 'completed');
        const firstCompletedWithRecords = completedPages.find(p =>
          response.records.some(r => r.pageId === p.id)
        );
        const pageToSelect = firstCompletedWithRecords || completedPages[0] || response.pages[0];
        setSelectedPageId(pageToSelect.id);
      }

      // Start polling if extraction is in progress
      if (isExtractionInProgress(response.status)) {
        startPolling();
      }
    } catch (err) {
      console.error('Failed to fetch batch:', err);
      setError('Failed to load batch extraction');
    } finally {
      setLoading(false);
    }
  }, [id, startPolling]);

  useEffect(() => {
    fetchBatch();
  }, [fetchBatch]);

  // Track page number separately so image loading doesn't depend on batch object
  const selectedPageNumber = useMemo(() => {
    if (!batch || !selectedPageId || selectedPageId === 'manual') return null;
    const page = batch.pages.find(p => p.id === selectedPageId);
    return page?.pageNumber ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally depend on batch.pages, not batch
  }, [batch?.pages, selectedPageId]);

  // Load image when selected page changes (not when batch data changes from cell edits)
  useEffect(() => {
    if (!id || !selectedPageId || selectedPageId === 'manual' || selectedPageNumber === null) return;

    setLoadingImage(true);
    setPageImageUrl(null);

    getPageImageUrl(id, selectedPageId, selectedPageNumber)
      .then(response => {
        setPageImageUrl(response.signedUrl);
      })
      .catch(err => {
        console.error('Failed to get page image URL:', err);
      })
      .finally(() => {
        setLoadingImage(false);
      });
  }, [id, selectedPageId, selectedPageNumber]);

  // Debounced save
  useEffect(() => {
    if (pendingUpdates.size === 0) return;

    const timeout = setTimeout(async () => {
      if (!id) return;

      // Snapshot exactly the record ids being sent so updates queued while this
      // save is in flight (e.g. a team assignment) are preserved, not clobbered
      // by a blanket clear.
      const sentRecordIds = Array.from(pendingUpdates.keys());
      const updates = Array.from(pendingUpdates.values());

      setSaving(true);
      try {
        await updateRecords(id, { records: updates });
        setPendingUpdates((prev) => {
          const next = new Map(prev);
          for (const recordId of sentRecordIds) {
            // Only drop the key if it hasn't been re-queued with a newer value
            // since we snapshotted (identity check on the queued update object).
            if (next.get(recordId) === pendingUpdates.get(recordId)) {
              next.delete(recordId);
            }
          }
          return next;
        });
      } catch (err) {
        console.error('Failed to save updates:', err);
      } finally {
        setSaving(false);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [pendingUpdates, id]);

  const teamNames = useMemo(() => {
    if (!batch) return [];
    const names = new Set<string>();
    batch.records.forEach((r) => {
      if (r.teamName) names.add(r.teamName);
    });
    return Array.from(names).sort();
  }, [batch]);

  // Teams on the currently selected page
  const pageTeamNames = useMemo(() => {
    if (!batch || !selectedPageId || selectedPageId === 'manual') return [];
    const names = new Set<string>();
    batch.records.forEach((r) => {
      if (r.pageId === selectedPageId && r.teamName) names.add(r.teamName);
    });
    return Array.from(names).sort();
  }, [batch, selectedPageId]);

  // Reset team filter when switching pages (the new page may not have the same teams)
  useEffect(() => {
    setFilterTeam('');
  }, [selectedPageId]);

  // Get records for selected page, optionally filtered to a team tab
  const selectedPageRecords = useMemo(() => {
    if (!batch || !selectedPageId) return [];

    return batch.records.filter((record) => {
      // Filter by page
      if (selectedPageId === 'manual') {
        if (record.pageId !== null) return false;
      } else {
        if (record.pageId !== selectedPageId) return false;
      }

      // Filter by team tab
      if (filterTeam && record.teamName !== filterTeam) {
        return false;
      }

      // Apply search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const searchFields = [
          record.firstName,
          record.lastName,
          record.teamName,
          record.email,
          record.school,
        ];
        return searchFields.some((f) => f?.toLowerCase().includes(search));
      }

      return true;
    });
  }, [batch, selectedPageId, filterTeam, searchTerm]);

  // Hide Team column when viewing a specific team tab (redundant info)
  const visibleFields = useMemo(() => {
    if (filterTeam) {
      return EDITABLE_FIELDS.filter(f => f.key !== 'teamName');
    }
    return EDITABLE_FIELDS;
  }, [filterTeam]);

  // Check if there are manual entries
  const hasManualEntries = useMemo(() => {
    if (!batch) return false;
    return batch.records.some(r => r.pageId === null);
  }, [batch]);

  const selectedPage = useMemo(() => {
    if (!batch || !selectedPageId || selectedPageId === 'manual') return null;
    return batch.pages.find(p => p.id === selectedPageId) || null;
  }, [batch, selectedPageId]);

  // Get display index (1-based) for the selected page
  const selectedPageDisplayIndex = useMemo(() => {
    if (!batch || !selectedPageId || selectedPageId === 'manual') return null;
    const sortedPages = batch.pages.slice().sort((a, b) => a.pageNumber - b.pageNumber);
    const index = sortedPages.findIndex(p => p.id === selectedPageId);
    return index >= 0 ? index + 1 : null;
  }, [batch, selectedPageId]);

  const handleStartEdit = useCallback((recordId: string, field: keyof BatchExtractionRecord) => {
    setEditingCell({ recordId, field });
  }, []);

  // Use ref to access current batch in callbacks without recreating them
  const batchRef = useRef(batch);
  useEffect(() => {
    batchRef.current = batch;
  }, [batch]);

  /**
   * The records the JUCO toggle acts on: this page, narrowed to the selected team tab.
   *
   * Deliberately NOT `selectedPageRecords` — that one also applies the search box, and marking a
   * roster JUCO while a search is active would classify only the rows that happened to match.
   */
  const jucoScopeRecords = useMemo(() => {
    if (!batch || !selectedPageId) return [];
    return batch.records.filter((record) => {
      if (selectedPageId === 'manual' ? record.pageId !== null : record.pageId !== selectedPageId) {
        return false;
      }
      return !filterTeam || record.teamName === filterTeam;
    });
  }, [batch, selectedPageId, filterTeam]);

  // `playerLevel` is 0 for High School, which is falsy — compare against the constant, never
  // test for truthiness.
  const jucoScopeMarked = useMemo(
    () => jucoScopeRecords.filter((r) => r.playerLevel === JUCO_PLAYER_LEVEL).length,
    [jucoScopeRecords]
  );
  const isJucoScope = jucoScopeRecords.length > 0 && jucoScopeMarked === jucoScopeRecords.length;
  const isMixedJucoScope = jucoScopeMarked > 0 && jucoScopeMarked < jucoScopeRecords.length;
  const jucoYearBlanks = useMemo(
    () =>
      jucoScopeRecords.filter(
        (r) => r.playerLevel === JUCO_PLAYER_LEVEL && !r.jucoYear
      ).length,
    [jucoScopeRecords]
  );

  const handleToggleJuco = useCallback(async (nextIsJuco: boolean) => {
    if (!id || jucoScopeRecords.length === 0) return;

    setJucoSubmitting(true);
    setError(null);
    try {
      await setRecordsJuco(id, {
        recordIds: jucoScopeRecords.map((r) => r.id),
        isJuco: nextIsJuco,
      });
      // The server re-infers each record's graduation year on the two-year ladder, so the local
      // copy is stale in a way no local edit can reproduce. Refetch rather than patch in place.
      await fetchBatch();
    } catch (err) {
      console.error('[BatchExtractionReview] Failed to set JUCO on records', {
        error: err,
        batchId: id,
        pageId: selectedPageId,
      });
      setError('Could not update the JUCO marking. Please try again.');
    } finally {
      setJucoSubmitting(false);
    }
  }, [id, jucoScopeRecords, selectedPageId, fetchBatch]);

  // Batch-wide counts for the all-pages actions: every record, on every page and team.
  const batchJucoMarked = useMemo(
    () => (batch ? batch.records.filter((r) => r.playerLevel === JUCO_PLAYER_LEVEL).length : 0),
    [batch]
  );
  const batchJucoYearBlanks = useMemo(
    () =>
      batch
        ? batch.records.filter((r) => r.playerLevel === JUCO_PLAYER_LEVEL && !r.jucoYear).length
        : 0,
    [batch]
  );
  // Mirrors the server's refusal: a page that has not written its records yet would be skipped.
  const anyPageExtracting = useMemo(
    () =>
      !!batch &&
      (batch.status === 'uploading' ||
        batch.status === 'splitting' ||
        batch.pages.some(
          (p) => p.status === 'pending' || p.status === 'assessing' || p.status === 'extracting'
        )),
    [batch]
  );

  const handleSetAllPagesJuco = useCallback(async (nextIsJuco: boolean) => {
    if (!id) return;

    setJucoSubmitting(true);
    setError(null);
    try {
      // No ids: the server selects every record in the batch.
      await setRecordsJuco(id, { recordIds: [], isJuco: nextIsJuco, scope: 'batch' });
      await fetchBatch();
    } catch (err) {
      console.error('[BatchExtractionReview] Failed to set JUCO on every page', {
        error: err,
        batchId: id,
      });
      setError(describeJucoActionError(err, 'Could not update JUCO on every page. Please try again.'));
    } finally {
      setJucoSubmitting(false);
    }
  }, [id, fetchBatch]);

  const handleFillJucoYear = useCallback(async (
    jucoYear: JucoYear,
    onlyBlank: boolean,
    scope: JucoScope
  ) => {
    if (!id) return;
    if (scope === 'page' && jucoScopeRecords.length === 0) return;

    setJucoSubmitting(true);
    setError(null);
    try {
      await fillRecordsJucoYear(id, {
        recordIds: scope === 'batch' ? [] : jucoScopeRecords.map((r) => r.id),
        jucoYear,
        onlyBlank,
        scope,
      });
      await fetchBatch();
      setShowJucoYearModal(false);
    } catch (err) {
      console.error('[BatchExtractionReview] Failed to fill JC year', {
        error: err,
        batchId: id,
        pageId: selectedPageId,
        scope,
      });
      setError(describeJucoActionError(err, 'Could not fill the JC year. Please try again.'));
    } finally {
      setJucoSubmitting(false);
    }
  }, [id, jucoScopeRecords, selectedPageId, fetchBatch]);

  const handleCommitEdit = useCallback((recordId: string, field: keyof BatchExtractionRecord, newValue: string) => {
    const currentBatch = batchRef.current;
    if (!currentBatch) {
      setEditingCell(null);
      return;
    }

    const record = currentBatch.records.find((r) => r.id === recordId);
    if (!record) {
      setEditingCell(null);
      return;
    }

    const currentValue = record[field];
    const hasChanged = newValue !== (currentValue?.toString() ?? '');

    if (!hasChanged) {
      setEditingCell(null);
      return;
    }

    // Prepare typed value
    let typedValue: unknown = newValue;
    if (field === 'jerseyNumber' || field === 'gradYear' || field === 'weight') {
      typedValue = newValue ? parseInt(newValue, 10) : null;
    }

    // Update local state - only create new reference for the edited record
    const updatedRecords = currentBatch.records.map((r) => {
      if (r.id !== recordId) return r;
      return { ...r, [field]: typedValue };
    });

    // Batch all state updates together
    setBatch({ ...currentBatch, records: updatedRecords });
    setPendingUpdates((prev) => {
      const existing = prev.get(recordId) || { id: recordId };
      const update: BatchExtractionRecordUpdate = {
        ...existing,
        [field]: newValue || undefined,
      };
      return new Map(prev).set(recordId, update);
    });
    setEditingCell(null);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingCell(null);
  }, []);

  // Assign a record to an existing team. Updates local state, caches the team
  // name for display, queues a `resolvedTeamId` update, and clears the record's
  // unresolved flag.
  const handleAssignTeam = useCallback((recordId: string, team: TeamOption) => {
    if (!team.id) return;
    const teamId = team.id;

    setBatch((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        records: prev.records.map((r) =>
          r.id === recordId ? { ...r, resolvedTeamId: teamId } : r
        ),
      };
    });
    setAssignedTeamNames((prev) => new Map(prev).set(recordId, team.name));
    setPendingUpdates((prev) => {
      const existing = prev.get(recordId) || { id: recordId };
      return new Map(prev).set(recordId, { ...existing, resolvedTeamId: teamId });
    });
    setUnresolvedRecordIds((prev) => {
      if (!prev.has(recordId)) return prev;
      const next = new Set(prev);
      next.delete(recordId);
      return next;
    });
  }, []);

  // Clear a record's team assignment. Sends the all-zeros sentinel so the API
  // unassigns it (vs. omitting, which would leave it unchanged).
  const handleClearTeam = useCallback((recordId: string) => {
    setBatch((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        records: prev.records.map((r) =>
          r.id === recordId ? { ...r, resolvedTeamId: null } : r
        ),
      };
    });
    setAssignedTeamNames((prev) => {
      if (!prev.has(recordId)) return prev;
      const next = new Map(prev);
      next.delete(recordId);
      return next;
    });
    setPendingUpdates((prev) => {
      const existing = prev.get(recordId) || { id: recordId };
      return new Map(prev).set(recordId, { ...existing, resolvedTeamId: CLEAR_RESOLVED_TEAM });
    });
  }, []);

  const handleDeleteRecord = useCallback(async (recordId: string) => {
    if (!id) return;

    try {
      await deleteRecord(id, recordId);
      setBatch((prevBatch) => {
        if (!prevBatch) return prevBatch;
        return {
          ...prevBatch,
          records: prevBatch.records.filter((r) => r.id !== recordId),
        };
      });
    } catch (err) {
      console.error('Failed to delete record:', err);
    }
  }, [id]);

  const handleRetryPage = async (pageId: string) => {
    if (!id || !batch) return;

    const recordCount = batch.records.filter((r) => r.pageId === pageId).length;

    if (recordCount > 0) {
      if (!window.confirm(`This page has ${recordCount} record(s). Retrying will delete them and re-extract. Continue?`)) {
        return;
      }
    }

    setRetryingPage({ id: pageId, status: 'pending' });

    // Update local state immediately - set page to pending and remove its records
    setBatch({
      ...batch,
      pages: batch.pages.map((p) =>
        p.id === pageId ? { ...p, status: 'pending', errorMessage: null, teamName: null } : p
      ),
      records: batch.records.filter((r) => r.pageId !== pageId),
    });

    try {
      await retryPage(id, pageId);

      // Poll for completion - update status as it progresses
      const pollInterval = setInterval(async () => {
        try {
          const updated = await getBatchExtraction(id);
          const updatedPage = updated.pages.find((p) => p.id === pageId);
          if (updatedPage) {
            // Update the displayed status
            setRetryingPage({ id: pageId, status: updatedPage.status });

            if (updatedPage.status === 'completed' || updatedPage.status === 'failed') {
              clearInterval(pollInterval);
              setBatch(updated);
              setRetryingPage(null);
            }
          }
        } catch {
          clearInterval(pollInterval);
          setRetryingPage(null);
        }
      }, 1500);

      // Safety timeout after 2 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setRetryingPage(null);
      }, 120000);
    } catch (err) {
      console.error('Failed to retry page:', err);
      setRetryingPage(null);
    }
  };

  const handleDeletePage = async (pageId: string) => {
    if (!id || !batch) return;

    if (!window.confirm('Are you sure you want to delete this page and all its records? This cannot be undone.')) {
      return;
    }

    setDeletingPageId(pageId);
    try {
      await deletePage(id, pageId);

      // Get the deleted page to check its status
      const deletedPage = batch.pages.find((p) => p.id === pageId);

      // Remove page (keep original pageNumber for API calls)
      const newPages = batch.pages.filter((p) => p.id !== pageId);

      // Filter out records from deleted page
      const newRecords = batch.records.filter((r) => r.pageId !== pageId);

      // Update stats
      const wasCompleted = deletedPage?.status === 'completed';
      const wasFailed = deletedPage?.status === 'failed';

      setBatch({
        ...batch,
        pages: newPages,
        records: newRecords,
        totalPages: batch.totalPages - 1,
        stats: {
          ...batch.stats,
          completedPages: wasCompleted ? batch.stats.completedPages - 1 : batch.stats.completedPages,
          failedPages: wasFailed ? batch.stats.failedPages - 1 : batch.stats.failedPages,
          // Recalculate unique teams from remaining records
          uniqueTeams: new Set(newRecords.map((r) => r.teamName).filter(Boolean)).size,
        },
      });

      // Select next available page
      if (selectedPageId === pageId) {
        const nextPage = newPages[0];
        setSelectedPageId(nextPage?.id || (hasManualEntries ? 'manual' : null));
      }

      // Remove deleted page from bulk selection
      setSelectedPageIds((prev) => {
        if (!prev.has(pageId)) return prev;
        const next = new Set(prev);
        next.delete(pageId);
        return next;
      });
    } catch (err) {
      console.error('Failed to delete page:', err);
    } finally {
      setDeletingPageId(null);
    }
  };

  const handleAddManualRecord = async (data: Record<string, unknown>) => {
    if (!id) return;

    try {
      const newRecord = await addManualRecord(id, data as never);
      if (batch) {
        setBatch({
          ...batch,
          records: [...batch.records, newRecord],
        });
      }
      setShowManualEntryModal(false);
    } catch (err) {
      console.error('Failed to add record:', err);
    }
  };

  const handleRetryFailed = async () => {
    if (!id) return;

    setRetrying(true);
    try {
      await retryExtraction(id);
      // Refresh batch data to show updated statuses
      await fetchBatch();
    } catch (err) {
      console.error('Failed to retry extraction:', err);
    } finally {
      setRetrying(false);
    }
  };

  const togglePageSelection = useCallback((pageId: string) => {
    setSelectedPageIds((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) {
        next.delete(pageId);
      } else {
        next.add(pageId);
      }
      return next;
    });
  }, []);

  const clearPageSelection = useCallback(() => {
    setSelectedPageIds(new Set());
  }, []);

  const toggleAllPages = useCallback(() => {
    if (!batch) return;
    const allIds = batch.pages.map((p) => p.id);
    setSelectedPageIds((prev) => (prev.size === allIds.length ? new Set() : new Set(allIds)));
  }, [batch]);

  const handleBulkSubmit = async (teamName: string | null, carryForward: boolean) => {
    if (!id || !batch || selectedPageIds.size === 0) return;

    // Compute the effective update list. When carry-forward is on, also include all
    // pages whose pageNumber is greater than the highest selected page AND whose
    // teamName is currently null/empty (never overwrites an explicit value).
    const sortedPages = batch.pages.slice().sort((a, b) => a.pageNumber - b.pageNumber);
    const selected = sortedPages.filter((p) => selectedPageIds.has(p.id));
    const highestSelectedPageNumber = selected.reduce(
      (max, p) => (p.pageNumber > max ? p.pageNumber : max),
      -Infinity,
    );

    const carryForwardPages = carryForward
      ? sortedPages.filter(
          (p) =>
            p.pageNumber > highestSelectedPageNumber &&
            !selectedPageIds.has(p.id) &&
            !p.teamName,
        )
      : [];

    const updates: PageTeamNameUpdate[] = [...selected, ...carryForwardPages].map((p) => ({
      pageId: p.id,
      teamName,
    }));

    setBulkSubmitting(true);
    try {
      await updatePageTeamNames(id, updates);
      // Refresh to pick up server state (authoritative after bulk write)
      await fetchBatch();
      clearPageSelection();
      setShowBulkTeamModal(false);
    } catch (err) {
      console.error('Failed to bulk update page team names:', err);
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleSavePageTeam = async (pageId: string) => {
    if (!id || !batch) return;

    try {
      const newValue = pageTeamValue.trim() || null;
      await updatePageTeamName(id, pageId, newValue);
      // Server propagates the new team name to records on this page; refresh to pick
      // up both the page change and the updated record.team_name for the records table.
      await fetchBatch();
    } catch (err) {
      console.error('Failed to update page team name:', err);
    } finally {
      setEditingPageTeam(null);
    }
  };

  const handleCancelPageTeam = () => {
    setEditingPageTeam(null);
    setPageTeamValue('');
  };

  const handlePanelResize = useCallback((deltaX: number) => {
    if (!splitViewRef.current) return;

    const containerWidth = splitViewRef.current.clientWidth;
    // Account for page list width (140px when expanded, 36px when collapsed)
    const pageListWidth = pagesCollapsed ? 36 : 140;
    const availableWidth = containerWidth - pageListWidth - 8; // 8px for resize handle

    // Convert delta to percentage of available width
    const deltaPercent = (deltaX / availableWidth) * 100;

    setImagePanelWidth(prev => {
      const newWidth = prev + deltaPercent;
      // Constrain between 20% and 70%
      return Math.min(70, Math.max(20, newWidth));
    });
  }, [pagesCollapsed]);

  const getPageRecordCount = (pageId: string) => {
    if (!batch) return 0;
    if (pageId === 'manual') {
      return batch.records.filter(r => r.pageId === null).length;
    }
    return batch.records.filter(r => r.pageId === pageId).length;
  };

  if (loading) {
    return (
      <AdminPage width="full" className="batch-review">
        <div className="admin-state">Loading batch...</div>
      </AdminPage>
    );
  }

  if (error || !batch) {
    return (
      <AdminPage width="full" className="batch-review">
        <div className="admin-error" role="alert">
          {error || 'Batch not found'}
          <button onClick={() => navigate('/admin/batch-extractions')}>Back to List</button>
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage width="full" className="batch-review">
      <AdminPageHeader
        back={
          <button
            className="batch-review__back-btn"
            onClick={() => navigate('/admin/batch-extractions')}
          >
            Back
          </button>
        }
        title={batch.name}
        meta={saving && <span className="batch-review__saving">Saving...</span>}
        actions={
          <>
              {selectedPageIds.size > 0 && (
                <div className="batch-review__selection-chip">
                  <span className="batch-review__selection-chip__count">
                    {selectedPageIds.size} page{selectedPageIds.size === 1 ? '' : 's'} selected
                  </span>
                  <button
                    type="button"
                    className="batch-review__selection-chip__clear"
                    onClick={clearPageSelection}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="batch-review__btn batch-review__btn--primary"
                    onClick={() => setShowBulkTeamModal(true)}
                  >
                    Set Team…
                  </button>
                </div>
              )}
              <button
                className="batch-review__btn batch-review__btn--secondary"
                onClick={() => setShowManualEntryModal(true)}
              >
                Add Record
              </button>
              <button
                className="batch-review__btn batch-review__btn--primary"
                onClick={() => setShowDistributeModal(true)}
              >
                Distribute to Organizations
              </button>
          </>
        }
      />

      {/* Extraction Progress Bar */}
      {isExtractionInProgress(batch.status) && (
        <div className="batch-review__progress">
          <div className="batch-review__progress-info">
            <span className="batch-review__progress-status">
              {batch.status === 'splitting' || batch.status === 'uploading'
                ? 'Splitting PDF...'
                : `Extracting: ${batch.stats.completedPages} of ${batch.totalPages} pages`}
            </span>
            {batch.status === 'extracting' && batch.totalPages > 0 && (
              <span className="batch-review__progress-percent">
                {Math.round((batch.stats.completedPages / batch.totalPages) * 100)}%
              </span>
            )}
          </div>
          <div className="batch-review__progress-bar">
            <div
              className={`batch-review__progress-fill ${batch.status === 'splitting' || batch.status === 'uploading' ? 'batch-review__progress-fill--indeterminate' : ''}`}
              style={{
                width: batch.status === 'extracting' && batch.totalPages > 0
                  ? `${(batch.stats.completedPages / batch.totalPages) * 100}%`
                  : undefined
              }}
            />
          </div>
        </div>
      )}

      {/* Collapsible Stats */}
      <div className={`batch-review__collapsible ${statsCollapsed ? 'batch-review__collapsible--collapsed' : ''}`}>
        <button
          className="batch-review__collapse-toggle"
          onClick={() => setStatsCollapsed(!statsCollapsed)}
          title={statsCollapsed ? 'Expand stats' : 'Collapse stats'}
        >
          <span className={`collapse-icon ${statsCollapsed ? 'collapse-icon--collapsed' : ''}`}>&#9662;</span>
          <span>Stats</span>
          {statsCollapsed && (
            <span className="batch-review__collapse-summary">
              {batch.records.length} records, {batch.stats.uniqueTeams} teams, {batch.stats.completedPages}/{batch.pages.length} pages
            </span>
          )}
        </button>
        {!statsCollapsed && (
          <div className="batch-review__stats">
            <div className="batch-review__stat">
              <span className="batch-review__stat-value">{batch.records.length}</span>
              <span className="batch-review__stat-label">Records</span>
            </div>
            <div className="batch-review__stat">
              <span className="batch-review__stat-value">{batch.stats.uniqueTeams}</span>
              <span className="batch-review__stat-label">Teams</span>
            </div>
            <div className="batch-review__stat">
              <span className="batch-review__stat-value">{batch.stats.completedPages}/{batch.pages.length}</span>
              <span className="batch-review__stat-label">Pages Extracted</span>
            </div>
            {batch.stats.failedPages > 0 && (
              <div className="batch-review__stat batch-review__stat--warning">
                <span className="batch-review__stat-value">{batch.stats.failedPages}</span>
                <span className="batch-review__stat-label">Failed Pages</span>
                <button
                  className="batch-review__retry-btn"
                  onClick={handleRetryFailed}
                  disabled={retrying}
                >
                  {retrying ? 'Retrying...' : 'Retry Failed'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Side-by-side view */}
      <div className="batch-review__split-view" ref={splitViewRef}>
        {/* Left side: Page list (collapsible) */}
        <div className={`batch-review__page-list ${pagesCollapsed ? 'batch-review__page-list--collapsed' : ''}`}>
          <div className="batch-review__page-list-header">
            <button
              className="batch-review__collapse-btn"
              onClick={() => setPagesCollapsed(!pagesCollapsed)}
              title={pagesCollapsed ? 'Expand pages' : 'Collapse pages'}
            >
              <span className={`collapse-icon ${pagesCollapsed ? 'collapse-icon--right' : ''}`}>&#9666;</span>
            </button>
            {!pagesCollapsed && <span>Pages</span>}
          </div>
          {!pagesCollapsed && (
            <div className="batch-review__page-list-items">
              {batch.pages.length > 0 && (
                <div className="page-list-select-all">
                  <label className="page-list-select-all__label" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="page-list-select-all__checkbox"
                      checked={selectedPageIds.size > 0 && selectedPageIds.size === batch.pages.length}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate =
                            selectedPageIds.size > 0 && selectedPageIds.size < batch.pages.length;
                        }
                      }}
                      onChange={toggleAllPages}
                      aria-label="Select all pages"
                    />
                    <span>Select all</span>
                  </label>
                </div>
              )}
              {batch.pages
                .slice()
                .sort((a, b) => a.pageNumber - b.pageNumber)
                .map((page, index) => (
                    <div
                      key={page.id}
                      className={`page-list-item ${selectedPageId === page.id ? 'page-list-item--selected' : ''} ${page.status === 'failed' ? 'page-list-item--failed' : ''} ${selectedPageIds.has(page.id) ? 'page-list-item--checked' : ''}`}
                      {...activate(() => setSelectedPageId(page.id))}
                    >
                      <div className="page-list-item__left">
                        <label
                          className="page-list-item__checkbox-label"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            className="page-list-item__checkbox"
                            checked={selectedPageIds.has(page.id)}
                            onChange={() => togglePageSelection(page.id)}
                            aria-label={`Select page ${index + 1}`}
                          />
                        </label>
                        <PageStatusIcon status={page.status} />
                        <div className="page-list-item__info">
                          <span className="page-list-item__number">Page {index + 1}</span>
                          {page.teamName && (
                            <span className="page-list-item__team">{page.teamName}</span>
                          )}
                        </div>
                      </div>
                      <span className="page-list-item__count">{getPageRecordCount(page.id)}</span>
                    </div>
                ))}
              {hasManualEntries && (
                <div
                  className={`page-list-item ${selectedPageId === 'manual' ? 'page-list-item--selected' : ''}`}
                  {...activate(() => setSelectedPageId('manual'))}
                >
                  <span className="page-list-item__number">Manual Entries</span>
                  <span className="page-list-item__count">{getPageRecordCount('manual')}</span>
                </div>
              )}
            </div>
          )}
          {pagesCollapsed && (
            <div className="batch-review__page-list-collapsed">
              {batch.pages
                .slice()
                .sort((a, b) => a.pageNumber - b.pageNumber)
                .map((page, index) => (
                    <div
                      key={page.id}
                      className={`page-list-dot ${selectedPageId === page.id ? 'page-list-dot--selected' : ''} ${page.status === 'failed' ? 'page-list-dot--failed' : ''} ${page.status === 'extracting' || page.status === 'assessing' ? 'page-list-dot--extracting' : ''}`}
                      {...activate(() => setSelectedPageId(page.id))}
                      title={`Page ${index + 1} (${getPageRecordCount(page.id)} records) - ${page.status}`}
                    >
                      {index + 1}
                    </div>
                ))}
              {hasManualEntries && (
                <div
                  className={`page-list-dot ${selectedPageId === 'manual' ? 'page-list-dot--selected' : ''}`}
                  {...activate(() => setSelectedPageId('manual'))}
                  title={`Manual Entries (${getPageRecordCount('manual')} records)`}
                >
                  M
                </div>
              )}
            </div>
          )}
        </div>

        {/* Middle: Page image */}
        <div
          className="batch-review__image-panel"
          style={{ flex: `0 0 ${imagePanelWidth}%` }}
        >
          <div className="batch-review__image-header">
            {selectedPage && selectedPageDisplayIndex && (
              <>
                <span>Page {selectedPageDisplayIndex} Image</span>
                <div className="batch-review__page-actions">
                  <button
                    className="batch-review__retry-page-btn"
                    onClick={() => handleRetryPage(selectedPage.id)}
                    disabled={retryingPage?.id === selectedPage.id || deletingPageId === selectedPage.id}
                    title="Re-extract this page"
                  >
                    {retryingPage?.id === selectedPage.id
                      ? formatRetryStatus(retryingPage.status)
                      : 'Retry'}
                  </button>
                  <button
                    className="batch-review__delete-page-btn"
                    onClick={() => handleDeletePage(selectedPage.id)}
                    disabled={deletingPageId === selectedPage.id || retryingPage?.id === selectedPage.id}
                  >
                    {deletingPageId === selectedPage.id ? 'Deleting...' : 'Delete Page'}
                  </button>
                </div>
              </>
            )}
            {selectedPageId === 'manual' && <span>Manual Entries</span>}
          </div>
          <div className="batch-review__image-container">
            {loadingImage && (
              <div className="batch-review__image-loading">Loading image...</div>
            )}
            {!loadingImage && pageImageUrl && selectedPageId !== 'manual' && (
              <ImageZoomViewer
                src={pageImageUrl}
                alt={`Page ${selectedPageDisplayIndex}`}
              />
            )}
            {selectedPageId === 'manual' && (
              <div className="batch-review__no-image">
                Manual entries have no source image
              </div>
            )}
            {!loadingImage && !pageImageUrl && selectedPageId !== 'manual' && (
              <div className="batch-review__no-image">
                No image available
              </div>
            )}
          </div>
        </div>

        <ResizeHandle onResize={handlePanelResize} />

        {/* Right side: Records table */}
        <div className="batch-review__records-panel" style={{ flex: 1 }}>
          <div className="batch-review__records-header">
            <div className="batch-review__records-header-left">
              <span>Records ({selectedPageRecords.length})</span>
              {/* Per-page team name control. Shown for single-team batches (0-1
                  teams overall) AND for any teamless page in a multi-team batch,
                  so a page with no detected team can be grouped under a team
                  already in this batch. Backed by a datalist of the batch's team
                  names so it's a select-or-type combobox, not free text only. */}
              {selectedPage && selectedPageId !== 'manual' && !filterTeam &&
                (teamNames.length <= 1 || !selectedPage.teamName) && (
                <div className="batch-review__page-team-inline">
                  <label htmlFor="batch-extraction-review-team-name-0" className="batch-review__page-team-label">Team Name:</label>
                  <input id="batch-extraction-review-team-name-0"
                    type="text"
                    className="batch-review__page-team-input-inline"
                    list={PAGE_TEAM_DATALIST_ID}
                    autoComplete="off"
                    value={editingPageTeam === selectedPage.id ? pageTeamValue : (selectedPage.teamName || '')}
                    placeholder={teamNames.length > 0 ? 'Select or type team name…' : 'Enter team name…'}
                    onFocus={() => {
                      setEditingPageTeam(selectedPage.id);
                      setPageTeamValue(selectedPage.teamName || '');
                    }}
                    onChange={(e) => setPageTeamValue(e.target.value)}
                    onBlur={() => handleSavePageTeam(selectedPage.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                      if (e.key === 'Escape') {
                        handleCancelPageTeam();
                        e.currentTarget.blur();
                      }
                    }}
                  />
                  <datalist id={PAGE_TEAM_DATALIST_ID}>
                    {teamNames.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                </div>
              )}
              {/* Per-team JUCO marking. Scoped to the page (and the team tab when one is
                  active) rather than the whole batch: a junior-college scout book routinely
                  contains a prep program, and a batch-level flag would misclassify it. */}
              {jucoScopeRecords.length > 0 && (
                <div className="batch-review__juco">
                  <label className="batch-review__juco-toggle">
                    <input
                      type="checkbox"
                      checked={isJucoScope}
                      ref={(el) => {
                        // Some rows marked and some not — usually a per-row override. Neither
                        // checked nor unchecked describes that honestly.
                        if (el) el.indeterminate = isMixedJucoScope;
                      }}
                      disabled={jucoSubmitting}
                      onChange={(e) => handleToggleJuco(e.target.checked)}
                    />
                    <span>
                      JUCO {filterTeam ? 'team' : 'page'}
                      {isMixedJucoScope && ` (${jucoScopeMarked}/${jucoScopeRecords.length})`}
                    </span>
                  </label>
                  {jucoScopeMarked > 0 && (
                    <button
                      type="button"
                      className="batch-review__juco-fill"
                      disabled={jucoSubmitting}
                      onClick={() => setShowJucoYearModal(true)}
                    >
                      Fill JC year
                      {jucoYearBlanks > 0 && ` (${jucoYearBlanks} blank)`}
                    </button>
                  )}
                </div>
              )}
              {/* Batch-wide JUCO, for a scout book where every team is a junior college. */}
              {batch.records.length > 0 && (
                <>
                  <BatchWideJucoActions
                    recordCount={batch.records.length}
                    markedCount={batchJucoMarked}
                    extracting={anyPageExtracting}
                    submitting={jucoSubmitting}
                    onConfirm={handleSetAllPagesJuco}
                  />
                  {jucoScopeMarked === 0 && batchJucoMarked > 0 && (
                    <button
                      type="button"
                      className="batch-review__juco-fill"
                      disabled={jucoSubmitting}
                      onClick={() => setShowJucoYearModal(true)}
                    >
                      Fill JC year
                      {batchJucoYearBlanks > 0 && ` (${batchJucoYearBlanks} blank)`}
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="batch-review__records-filters">
              <input
                type="text"
                className="batch-review__mini-search"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          {/* Team tabs - shown when page has multiple teams */}
          {pageTeamNames.length > 1 && (
            <div className="batch-review__team-tabs">
              <button
                className={`batch-review__team-tab ${!filterTeam ? 'batch-review__team-tab--active' : ''}`}
                onClick={() => setFilterTeam('')}
              >
                All
              </button>
              {pageTeamNames.map((name) => (
                <button
                  key={name}
                  className={`batch-review__team-tab ${filterTeam === name ? 'batch-review__team-tab--active' : ''}`}
                  onClick={() => setFilterTeam(name)}
                >
                  {name}
                  <span className="batch-review__team-tab-count">
                    {batch.records.filter(r => r.pageId === selectedPageId && r.teamName === name).length}
                  </span>
                </button>
              ))}
            </div>
          )}
          <div className="batch-review__records-table-container">
            <table className="review-table">
              <thead>
                <tr>
                  <th className="review-table__th--actions"></th>
                  <th className="review-table__th--team-assignment" style={{ width: '170px' }}>
                    Team Assignment
                  </th>
                  {visibleFields.map((field) => (
                    <th key={field.key} style={{ width: field.width }}>
                      {field.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {selectedPageRecords.length === 0 ? (
                  <tr>
                    <td colSpan={visibleFields.length + 2} className="review-table__empty">
                      {filterTeam ? 'No records for this team' : 'No records on this page'}
                    </td>
                  </tr>
                ) : (
                  selectedPageRecords.map((record) => (
                    <RecordRow
                      key={record.id}
                      record={record}
                      editableFields={visibleFields}
                      editingField={editingCell?.recordId === record.id ? editingCell.field : null}
                      onStartEdit={handleStartEdit}
                      onCommitEdit={handleCommitEdit}
                      onCancelEdit={handleCancelEdit}
                      onDelete={handleDeleteRecord}
                      showTeamAssignment
                      assignedTeamName={assignedTeamNames.get(record.id) ?? null}
                      teamUnresolved={unresolvedRecordIds.has(record.id)}
                      jucoYearMissing={jucoYearMissingIds.has(record.id)}
                      onAssignTeam={handleAssignTeam}
                      onClearTeam={handleClearTeam}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showDistributeModal && (
        <DistributeModal
          batchId={batch.id}
          distributions={batch.distributions}
          onClose={() => setShowDistributeModal(false)}
          onDistributed={fetchBatch}
          onUnresolvedRecords={(recordIds) => setUnresolvedRecordIds(new Set(recordIds))}
          onJucoYearMissing={(recordIds) => setJucoYearMissingIds(new Set(recordIds))}
        />
      )}

      {showJucoYearModal && (

        <BulkJucoYearModal

          blankCount={jucoYearBlanks}

          markedCount={jucoScopeMarked}

          allPages={{
            blankCount: batchJucoYearBlanks,
            markedCount: batchJucoMarked,
            disabled: anyPageExtracting,
          }}

          pageScopeLabel={filterTeam ? 'This team' : 'This page'}

          submitting={jucoSubmitting}

          onClose={() => setShowJucoYearModal(false)}

          onSubmit={handleFillJucoYear}

        />

      )}

      {showManualEntryModal && (
        <ManualEntryModal
          teamNames={teamNames}
          onClose={() => setShowManualEntryModal(false)}
          onSave={handleAddManualRecord}
        />
      )}

      {showBulkTeamModal && batch && (
        <BulkSetTeamModal
          selectedCount={selectedPageIds.size}
          teamNames={teamNames}
          submitting={bulkSubmitting}
          onClose={() => setShowBulkTeamModal(false)}
          onSubmit={handleBulkSubmit}
        />
      )}
    </AdminPage>
  );
};

const PageStatusIcon = ({ status }: { status: BatchExtractionPage['status'] }) => {
  switch (status) {
    case 'pending':
      return <span className="page-status-icon page-status-icon--pending" title="Pending">&#9711;</span>;
    case 'assessing':
    case 'extracting':
      return <span className="page-status-icon page-status-icon--extracting" title="Extracting"><span className="spinner-small" /></span>;
    case 'completed':
      return <span className="page-status-icon page-status-icon--completed" title="Completed">&#10003;</span>;
    case 'failed':
      return <span className="page-status-icon page-status-icon--failed" title="Failed">&#10007;</span>;
    default:
      return null;
  }
};
